import { useCallback, useState } from 'react';
import { DataProviderInstance } from '../../../api/DataProvider';
import { getCurrentUser } from '../../../utils/RoleManager';
import { USER_ROLES } from '../../../constants/userRoles';

export const AGENDA_VIEW = { MINE: 'mine', ALL: 'all' };

const storageKey = (companyId) => `dashboard.agendaView.${companyId}`;

/**
 * Filtro "Minha agenda / Todas" do Início. Só existe quando a conta tem mais de uma
 * agenda e a pessoa tem uma agenda própria e vê todas (quem está em "Só os meus
 * pacientes" já recebe só a própria agenda da API). O profissional de saúde abre na
 * própria agenda; proprietário e administrador abrem em todas. A escolha fica salva
 * no navegador, por empresa.
 */
export default function useAgendaView() {
  const { CompanyConfig, session } = DataProviderInstance();
  const user = getCurrentUser();
  const companyId = session?.currentCompany;

  const ownResourceId = user?.resourceId ?? null;
  const ownScope = !!user?.isHealthProfessional && user?.scheduleScope === 'own';
  const activeResources = (CompanyConfig?.Resources || []).filter((r) => r.Active == null || Number(r.Active) === 1);
  const showFilter = activeResources.length > 1 && ownResourceId != null && !ownScope;

  const isManager = [USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN].includes(user?.role);
  const defaultView = user?.isHealthProfessional && !isManager ? AGENDA_VIEW.MINE : AGENDA_VIEW.ALL;

  const [chosen, setChosen] = useState(() => {
    try {
      const saved = window.localStorage.getItem(storageKey(companyId));
      return saved === AGENDA_VIEW.MINE || saved === AGENDA_VIEW.ALL ? saved : null;
    } catch (e) {
      return null;
    }
  });

  const setView = useCallback((next) => {
    setChosen(next);
    try {
      window.localStorage.setItem(storageKey(companyId), next);
    } catch (e) {
      // navegador sem armazenamento: a escolha vale só nesta visita
    }
  }, [companyId]);

  const view = showFilter ? (chosen || defaultView) : AGENDA_VIEW.ALL;
  const isMine = view === AGENDA_VIEW.MINE;

  /** O item (consulta, pedido, reserva) é da minha agenda? */
  const isOwnItem = useCallback((item) => {
    const resource = item?.resource ?? item?.professional?.id ?? null;
    return resource != null && resource !== '' && String(resource) === String(ownResourceId);
  }, [ownResourceId]);

  /** Lista conforme o filtro: na "Minha agenda", só o que é da agenda da pessoa. */
  const filterList = useCallback((list) => (
    isMine ? (list || []).filter(isOwnItem) : (list || [])
  ), [isMine, isOwnItem]);

  return { view, setView, showFilter, isMine, ownResourceId, filterList };
}
